import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { api } from '../api/client';
import type { ImportantDateFormValues, ImportantDateType, Person } from '../api/types';
import { SectionHeading } from '../components/SectionHeading';
import { StatusBanner } from '../components/StatusBanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { defaultDateFormValues, toDateFormValues, toDatePayload, validateDateForm } from '../features/v1/forms';
import { formatImportantDateTypeLabel } from '../utils/format';

type ImportantDateFormPageProps = {
  mode: 'create' | 'edit';
};

const fieldClass =
  'mt-2 flex min-h-[52px] w-full rounded-[2px] border border-input bg-card px-4 py-3 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const dateTypes: ImportantDateType[] = [
  'birthday',
  'anniversary',
  'memorial',
  'petBirthday',
  'adoption',
  'vaccination',
  'appointment',
  'dday',
  'custom',
];

export const ImportantDateFormPage = ({ mode }: ImportantDateFormPageProps) => {
  const { id = '', personId = '' } = useParams();
  const navigate = useNavigate();
  const [values, setValues] = useState<ImportantDateFormValues>(defaultDateFormValues);
  const [person, setPerson] = useState<Person | null>(null);
  const [datePersonId, setDatePersonId] = useState(personId);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        if (mode === 'create') {
          const personResult = await api.getPersonById(personId);
          setPerson(personResult);
          setDatePersonId(personResult.id);
        } else {
          const date = await api.getImportantDateById(id);
          setValues(toDateFormValues(date));
          setDatePersonId(date.personId);
          setPerson(date.person);
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : '날짜 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [id, mode, personId]);

  const setField =
    <K extends keyof ImportantDateFormValues>(key: K) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const target = event.target;
      const nextValue = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value;
      setValues({ ...values, [key]: nextValue } as ImportantDateFormValues);
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateDateForm(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = toDatePayload(values);
      const saved =
        mode === 'create' ? await api.createPersonDate(datePersonId, payload) : await api.updateImportantDate(id, payload);
      navigate(`/people/${saved.personId}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : '저장하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (mode !== 'edit') return;
    const confirmed = window.confirm('이 중요 날짜를 삭제할까요? 이 작업은 되돌릴 수 없습니다.');
    if (!confirmed) return;
    setDeleting(true);
    try {
      await api.deleteImportantDate(id);
      navigate(`/people/${datePersonId}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : '삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionHeading
        eyebrow={person?.name ?? 'Important Date'}
        title={mode === 'create' ? '중요 날짜 추가' : '중요 날짜 수정'}
        description="양력/음력, 반복, 알림 기준일을 v1 API 계약에 맞춰 저장합니다."
      />
      {error ? <StatusBanner title="폼 작업에 실패했습니다" tone="danger" description={error} /> : null}
      {loading ? (
        <Card className="p-6 text-sm font-semibold text-muted-foreground">불러오는 중...</Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>날짜 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>날짜 유형</Label>
                <select className={fieldClass} value={values.type} onChange={setField('type')}>
                  {dateTypes.map((type) => (
                    <option key={type} value={type}>
                      {formatImportantDateTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>제목</Label>
                <Input value={values.title} onChange={setField('title')} placeholder="예: 음력 생신, 입양일" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>연도</Label>
                  <Input inputMode="numeric" value={values.originalYear} onChange={setField('originalYear')} placeholder="1988" />
                </div>
                <div>
                  <Label>월</Label>
                  <Input inputMode="numeric" value={values.originalMonth} onChange={setField('originalMonth')} placeholder="3" />
                </div>
                <div>
                  <Label>일</Label>
                  <Input inputMode="numeric" value={values.originalDay} onChange={setField('originalDay')} placeholder="16" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>달력</Label>
                  <select className={fieldClass} value={values.calendarType} onChange={setField('calendarType')}>
                    <option value="solar">양력</option>
                    <option value="lunar">음력</option>
                  </select>
                </div>
                <div>
                  <Label>반복</Label>
                  <select className={fieldClass} value={values.repeat} onChange={setField('repeat')}>
                    <option value="annual">매년</option>
                    <option value="none">반복 없음</option>
                  </select>
                </div>
              </div>
              <div className="grid gap-3 border-y border-border bg-muted/60 p-4">
                <label className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>윤달</span>
                  <input type="checkbox" checked={values.isLeapMonth} onChange={setField('isLeapMonth')} disabled={values.calendarType !== 'lunar'} />
                </label>
                <label className="flex items-center justify-between gap-3 text-sm font-semibold">
                  <span>알림 사용</span>
                  <input type="checkbox" checked={values.reminderEnabled} onChange={setField('reminderEnabled')} />
                </label>
                <div>
                  <Label>며칠 전 알림</Label>
                  <Input inputMode="numeric" value={values.reminderOffsetDays} onChange={setField('reminderOffsetDays')} placeholder="0" />
                </div>
              </div>
              <div>
                <Label>메모</Label>
                <textarea className={`${fieldClass} min-h-32`} value={values.memo} onChange={setField('memo')} placeholder="챙길 내용" />
              </div>
              <div>
                <Label>태그</Label>
                <Input value={values.tags} onChange={setField('tags')} placeholder="예: 음력, 가족" />
              </div>
            </CardContent>
          </Card>
          <div className="grid gap-2">
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? '저장 중...' : mode === 'create' ? '날짜 추가' : '변경 저장'}
            </Button>
            {mode === 'edit' ? (
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {deleting ? '삭제 중' : '날짜 삭제'}
              </Button>
            ) : null}
            <Button asChild type="button" variant="outline">
              <Link to={datePersonId ? `/people/${datePersonId}` : '/people'}>취소</Link>
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
